import { Injectable, signal } from '@angular/core';
import { ProductDetails } from '@models';
import { FormState } from '@shared';

export interface ProductListState {
  products: ProductDetails[];
  totalCount: number;
  filters: FormState;
  scrollY: number;
  routeUrl?: string; // Store the route URL to match state to correct page
}

/**
 * Service to store product list state for restoration after navigation.
 * This allows components to be destroyed/recreated (virtualized) while preserving state.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductListStateService {
  // Store state by normalized route URL to support multiple product lists
  private stateCache = new Map<string, ProductListState>();

  // Current state being restored (cleared after use)
  private pendingRestoreState = signal<ProductListState | null>(null);

  /**
   * Normalize route URL to handle query parameter differences
   * Extracts the pathname and essential query params (categoryId) for matching;
   * brand pages are already unique by pathname (`marketplace/:id`)
   */
  private normalizeRouteUrl(routeUrl: string): string {
    try {
      // Handle relative URLs (Angular router.url returns relative paths)
      const [pathname, queryString = ''] = routeUrl.split('?');
      const searchParams = new URLSearchParams(queryString);

      // Extract essential query params (categoryId for category pages)
      const categoryId = searchParams.get('categoryId');

      // Build normalized key: pathname + essential params only
      const normalizedParts = [pathname];
      if (categoryId) {
        normalizedParts.push(`categoryId=${categoryId}`);
      }

      const normalized = normalizedParts.join('?');
      return normalized;
    } catch (error) {
      // Fallback: use pathname only if parsing fails
      const pathname = routeUrl.split('?')[0];
      return pathname;
    }
  }

  /**
   * Save state for a given route
   */
  saveState(routeUrl: string, state: ProductListState): void {
    const normalizedKey = this.normalizeRouteUrl(routeUrl);
    this.stateCache.set(normalizedKey, state);
  }

  /**
   * Get and consume state for a route (removes it after retrieval)
   * Tries exact match first, then normalized match
   */
  getAndConsumeState(routeUrl: string): ProductListState | null {
    // Try exact match first
    let state = this.stateCache.get(routeUrl);

    if (!state) {
      // Try normalized match
      const normalizedKey = this.normalizeRouteUrl(routeUrl);
      state = this.stateCache.get(normalizedKey);

      if (state) {
        this.stateCache.delete(normalizedKey);
        this.pendingRestoreState.set(state);
        return state;
      }
    } else {
      this.stateCache.delete(routeUrl);
      this.pendingRestoreState.set(state);
      return state;
    }

    return null;
  }

  /**
   * Get pending restore state (for use in component constructor)
   */
  getPendingRestoreState(): ProductListState | null {
    const state = this.pendingRestoreState();
    if (state) {
      // Clear after retrieval
      this.pendingRestoreState.set(null);
      return state;
    }
    return null;
  }

  /**
   * Clear state for a specific route
   */
  clearState(routeUrl: string): void {
    const normalizedKey = this.normalizeRouteUrl(routeUrl);
    // Try both exact and normalized
    this.stateCache.delete(routeUrl);
    this.stateCache.delete(normalizedKey);
  }

  /**
   * Clear all cached state
   */
  clearAll(): void {
    this.stateCache.clear();
    this.pendingRestoreState.set(null);
  }
}
